import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Clock, Lock, ListChecks } from "lucide-react";

const BG_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_mls-deadline-master/artifacts/gwkjr2km_home2.jpg";
const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_mls-deadline-master/artifacts/jbrp9xqq_equitasfit%20%28Logo%29%20%284%29.png";

export default function Signup() {
  useDocumentTitle();
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    const res = await register(email, password, name);
    setBusy(false);
    if (res.ok) {
      toast.success("Account created");
      nav("/app");
    } else {
      toast.error(res.error || "Registration failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-10 px-6 lg:px-12 py-10 lg:py-16">
        <section className="lg:col-span-7 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Equitas 1%" className="h-11 w-11 rounded-full object-cover shadow-sm" />
            <div className="flex flex-col leading-none">
              <span className="overline text-stone-500">Equitas Homes</span>
              <span className="text-base font-heading font-semibold tracking-tight text-stone-900 mt-1">
                Listing Workflow
              </span>
            </div>
          </div>

          <div className="mt-14 lg:mt-24 max-w-2xl">
            <p className="overline text-stone-500 mb-6">Start Tracking</p>
            <h1 className="font-heading font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900">
              A dedicated workflow for <span className="font-serif-italic text-brand">every home</span> you take.
            </h1>
            <p className="mt-6 text-stone-600 text-base leading-relaxed max-w-xl">
              Seven phases, from seller intake to post-closing follow-up.
              Manual per-task deadlines, overdue flags, and a clean
              per-listing view your seller would actually understand.
            </p>

            <div className="mt-8 flex flex-wrap gap-2">
              <span className="chip">
                <Clock className="h-3.5 w-3.5" />
                Free to start
              </span>
              <span className="chip">
                <Lock className="h-3.5 w-3.5" />
                Private &amp; confidential
              </span>
              <span className="chip">
                <ListChecks className="h-3.5 w-3.5" />
                7 short phases
              </span>
            </div>
          </div>

          <div className="mt-14 max-w-md">
            <div className="rounded-md border border-stone-200 bg-white p-5 flex items-start gap-4">
              <img
                src={LOGO_URL}
                alt="Louis Fong"
                className="h-14 w-14 rounded-full object-cover shrink-0 shadow-sm"
              />
              <div>
                <p className="text-sm font-medium text-stone-900">Louis Fong</p>
                <p className="text-xs text-stone-500 mt-0.5">Realtor · Equitas Homes</p>
                <p className="font-serif-italic text-stone-700 text-sm leading-relaxed mt-2.5">
                  “Sharing the full picture up front is the biggest lift for your sale.”
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="lg:col-span-5 flex flex-col gap-8">
          <div className="relative rounded-md overflow-hidden aspect-[4/3] hidden lg:block">
            <img src={BG_URL} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute bottom-4 left-4">
              <span className="chip chip-dark">Interior · Craftsman</span>
            </div>
          </div>

          <div className="rounded-md border border-stone-200 bg-white p-7">
            <p className="overline text-stone-500 mb-3">Get started</p>
            <h2 className="text-2xl font-heading font-semibold tracking-tight text-stone-900">
              Create your account
            </h2>
            <p className="text-sm text-stone-500 mt-1.5 mb-6">
              Free — no credit card required.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-stone-700">Full name</Label>
                <Input
                  id="name"
                  data-testid="signup-name-input"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Alex Rivera"
                  required
                  className="h-11 rounded-md border-stone-300 bg-[#faf7f2]/40"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-stone-700">Email</Label>
                <Input
                  id="email"
                  data-testid="signup-email-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@brokerage.com"
                  required
                  className="h-11 rounded-md border-stone-300 bg-[#faf7f2]/40"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-stone-700">Password</Label>
                <Input
                  id="password"
                  data-testid="signup-password-input"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  className="h-11 rounded-md border-stone-300 bg-[#faf7f2]/40"
                />
              </div>

              <Button
                data-testid="signup-submit-button"
                type="submit"
                disabled={busy}
                className="w-full h-11 text-white rounded-full"
                style={{ backgroundColor: "#010082" }}
              >
                {busy ? "Creating account..." : "Create account"}
              </Button>
            </form>

            <p className="mt-6 text-sm text-stone-600 text-center">
              Already registered?{" "}
              <Link
                to="/login"
                data-testid="go-to-login-link"
                className="font-medium underline underline-offset-4"
                style={{ color: "#010082" }}
              >
                Log in
              </Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
